import React, { Suspense, useState, useCallback, useEffect, lazy, memo } from 'react';
import {
    ITourSelectControllerModel,
    ITourSelectControllerProps,
    ITourSelectType,
} from './interface';
import modelPlaceholderData from '@monofe/ui-workspace/tourselect/placeholderData';
import { ITourDto } from '@monofe/apiservice/model';
import LoadingView from '@monofe/ui-workspace/common/Loading';
import type IMapping from './mapping';
import { injectLog } from '../../interface/util';
import { ITourSelectViewModel } from '@monofe/ui-workspace/tourselect/interface';
import { getProjectOverrides } from '../../interface/overrides';
import { AnimatePresence } from 'framer-motion';
const isEqual = require('react-fast-compare');
const MainView = lazy(() => import('@monofe/ui-workspace/tourselect'));
const loadMapper = (): Promise<typeof IMapping> => {
    return new Promise(function (resolve, reject) {
        import('./mapping')
            .then(controller => resolve(controller.default))
            .catch(error => reject(error));
    });
};

/* max draw offset at which it is still visible during scrolling */
const TOPBAR_TEXT_OPACITY_MAX_OFFSET = 120;

interface ITourSelectController {
    model: ITourSelectControllerModel;
    type: ITourSelectType;
    isLoading: boolean;
}

const controllerPlaceholderData = (): ITourSelectController => ({
    model: {
        ...modelPlaceholderData(),
    },
    type: ITourSelectType.List,
    isLoading: true,
});

function View(props: ITourSelectControllerProps): JSX.Element {
    const log = injectLog();

    const [state, setState] = useState<ITourSelectController>(controllerPlaceholderData());

    const onTourSelect = useCallback(
        (tour: ITourDto) => props.didRequestTour(tour.id),
        [props.didRequestTour],
    );

    const onScroll = (position: { left: number; top: number }) => {
        const newOpacityPC =
            1 -
            Math.min(Math.max(position.top, 0), TOPBAR_TEXT_OPACITY_MAX_OFFSET) /
                TOPBAR_TEXT_OPACITY_MAX_OFFSET;
        setState(stateIn => ({
            ...stateIn,
            model: {
                ...stateIn.model,
                title: {
                    ...stateIn.model.title,
                    opacity: newOpacityPC,
                },
            },
        }));
    };

    useEffect(() => {
        loadMapper()
            .then(mapper => mapper(props.route, props.didRequestBack, onTourSelect, onScroll))
            .then(function ([model, type]) {
                return setState({
                    model: getProjectOverrides<ITourSelectViewModel>('ITourSelectViewModel', model),
                    type,
                    isLoading: false,
                });
            })
            .then(_ => log.info('Loaded TourSelect'))
            .catch(error => log.error(`Failed to load TourSelect ${error}`));
    }, [props.route]);

    useEffect(() => {
        document.title = state.model.title.text;
    }, [state.model.title.text]);

    return (
        <AnimatePresence>
            {state.isLoading ? (
                <LoadingView
                    styleOverrides={{
                        opacity: 1,
                        pointerEvents: 'auto',
                    }}
                />
            ) : (
                <Suspense fallback={null}>
                    <MainView key="TourSelect" type={state.type} model={state.model} />
                </Suspense>
            )}
        </AnimatePresence>
    );
}

function propsEqual(prev: ITourSelectControllerProps, next: ITourSelectControllerProps): boolean {
    const match = isEqual(prev, next);
    return match;
}

export default memo(View, propsEqual) as typeof View;
